import type {
  AuthCtx,
  HybridSearchOptions,
  SearchFilter,
  SearchHit,
  SearchResponse,
  SearchTierConfig,
  SearchOptions,
  VectorSearchOptions,
} from "@gencow/core";
import { sql, type SQL } from "drizzle-orm";
import type { RagMetricInput } from "./rag-operations-metrics.js";
import {
  buildLexicalClause,
  buildLexicalScore,
  createLexicalPlan,
  type LexicalPlan,
  type LexicalStrategy,
} from "./server-search-lexical-plan.js";

const DEFAULT_SEARCH_LIMIT = 20;
const MAX_SEARCH_LIMIT = 100;
const RRF_K = 60;
const IDENTIFIER_RE = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
const DEFAULT_FUSION_WEIGHT = 1;

type SearchDbLike = {
  execute(query: SQL): Promise<unknown>;
};

type SearchRuntimeDeps = {
  db: SearchDbLike;
  auth: AuthCtx;
  config: SearchTierConfig;
  observeRagOperation?: (event: Omit<RagMetricInput, "appId">) => Promise<void> | void;
};

type SearchDbRow = Record<string, unknown> & {
  id: string | number;
  score?: number | string | null;
  keyword_score?: number | string | null;
  vector_score?: number | string | null;
  fused_score?: number | string | null;
};

const INTERNAL_SEARCH_ROW_KEYS = ["score", "keyword_score", "vector_score", "fused_score"] as const;
type SearchMode = NonNullable<SearchResponse["meta"]["mode"]>;
type SearchFusionMode = NonNullable<HybridSearchOptions["fusion"]>;
type NormalizedVectorTuning = {
  minScore?: number;
};
type NormalizedHybridTuning = {
  fusion: SearchFusionMode;
  rrfK: number;
  keywordWeight: number;
  vectorWeight: number;
  keywordCandidateLimit: number;
  vectorCandidateLimit: number;
  minFusedScore?: number;
};

export class SearchRuntimeError extends Error {
  status: number;
  code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "SearchRuntimeError";
    this.status = status;
    this.code = code;
  }
}

function rowsFromExecute<T>(result: unknown): T[] {
  if (Array.isArray(result)) {
    return result as T[];
  }
  if (result && typeof result === "object" && Array.isArray((result as { rows?: unknown[] }).rows)) {
    return (result as { rows: T[] }).rows;
  }
  return [];
}

function quoteIdentifier(identifier: string): string {
  return `"${identifier}"`;
}

function requireSafeIdentifier(identifier: string, errorCode = "SEARCH_INVALID_IDENTIFIER"): string {
  if (!IDENTIFIER_RE.test(identifier)) {
    throw new SearchRuntimeError(400, errorCode, `Invalid identifier: ${identifier}`);
  }
  return quoteIdentifier(identifier);
}

function resolveLexicalPlan(config: Pick<SearchTierConfig, "engine" | "locale">, query: string): LexicalPlan {
  const plan = createLexicalPlan(config, query);
  if (!plan.normalizedQuery) {
    throw new SearchRuntimeError(400, "SEARCH_QUERY_REQUIRED", "Search query is required");
  }
  return plan;
}

function normalizeLimit(limit?: number): number {
  if (limit === undefined) return DEFAULT_SEARCH_LIMIT;
  if (!Number.isInteger(limit) || limit <= 0) {
    throw new SearchRuntimeError(400, "SEARCH_INVALID_LIMIT", "limit must be a positive integer");
  }
  return Math.min(limit, MAX_SEARCH_LIMIT);
}

function normalizeOffset(offset?: number): number {
  if (offset === undefined) return 0;
  if (!Number.isInteger(offset) || offset < 0) {
    throw new SearchRuntimeError(400, "SEARCH_INVALID_OFFSET", "offset must be a non-negative integer");
  }
  return offset;
}

function normalizePositiveInteger(
  value: number | undefined,
  errorCode: string,
  label: string,
): number | undefined {
  if (value === undefined) return undefined;
  if (!Number.isInteger(value) || value <= 0) {
    throw new SearchRuntimeError(400, errorCode, `${label} must be a positive integer`);
  }
  return value;
}

function normalizeFiniteNumber(
  value: number | undefined,
  errorCode: string,
  label: string,
): number | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new SearchRuntimeError(400, errorCode, `${label} must be a finite number`);
  }
  return value;
}

function normalizePositiveNumber(
  value: number | undefined,
  errorCode: string,
  label: string,
): number | undefined {
  const normalized = normalizeFiniteNumber(value, errorCode, label);
  if (normalized === undefined) return undefined;
  if (normalized <= 0) {
    throw new SearchRuntimeError(400, errorCode, `${label} must be greater than 0`);
  }
  return normalized;
}

function joinSql(chunks: SQL[], separator: string): SQL {
  if (chunks.length === 0) {
    return sql`true`;
  }

  let current = chunks[0];
  for (const chunk of chunks.slice(1)) {
    current = sql`${current}${sql.raw(separator)}${chunk}`;
  }
  return current;
}

function buildScopeClauses(auth: AuthCtx, scope: SearchOptions["scope"]): SQL[] {
  if (!scope.corpus.trim()) {
    throw new SearchRuntimeError(400, "SEARCH_SCOPE_REQUIRED", "scope.corpus is required");
  }

  if (!["private", "shared", "public"].includes(scope.visibility)) {
    throw new SearchRuntimeError(400, "SEARCH_INVALID_SCOPE", `Invalid visibility: ${scope.visibility}`);
  }

  const clauses: SQL[] = [
    sql`${sql.raw(requireSafeIdentifier("corpus", "SEARCH_INVALID_SCOPE_IDENTIFIER"))} = ${scope.corpus.trim()}`,
    sql`${sql.raw(requireSafeIdentifier("visibility_scope", "SEARCH_INVALID_SCOPE_IDENTIFIER"))} = ${scope.visibility}`,
  ];

  if (scope.visibility === "private") {
    const identity = auth.requireAuth();
    const effectiveOwnerUserId = scope.ownerUserId ?? identity.id;
    if (effectiveOwnerUserId !== identity.id) {
      throw new SearchRuntimeError(
        400,
        "SEARCH_SCOPE_OWNER_MISMATCH",
        "scope.ownerUserId must match the authenticated user for private searches",
      );
    }
    clauses.push(
      sql`${sql.raw(requireSafeIdentifier("owner_user_id", "SEARCH_INVALID_SCOPE_IDENTIFIER"))} = ${effectiveOwnerUserId}`,
    );
    return clauses;
  }

  if (scope.ownerUserId) {
    clauses.push(
      sql`${sql.raw(requireSafeIdentifier("owner_user_id", "SEARCH_INVALID_SCOPE_IDENTIFIER"))} = ${scope.ownerUserId}`,
    );
  } else {
    clauses.push(
      sql`${sql.raw(requireSafeIdentifier("owner_user_id", "SEARCH_INVALID_SCOPE_IDENTIFIER"))} IS NULL`,
    );
  }

  return clauses;
}

function buildFilterClauses(filters?: SearchOptions["filters"]): SQL[] {
  if (!filters) {
    return [];
  }

  const clauses: SQL[] = [];

  for (const [field, value] of Object.entries(filters.eq ?? {})) {
    clauses.push(sql`${sql.raw(requireSafeIdentifier(field))} = ${value}`);
  }

  for (const [field, values] of Object.entries(filters.in ?? {})) {
    if (!Array.isArray(values) || values.length === 0) {
      throw new SearchRuntimeError(
        400,
        "SEARCH_INVALID_FILTER",
        `${field} filter "in" requires at least one value`,
      );
    }
    const placeholders = joinSql(
      values.map((value) => sql`${value}`),
      ", ",
    );
    clauses.push(sql`${sql.raw(requireSafeIdentifier(field))} in (${placeholders})`);
  }

  for (const [field, rawRange] of Object.entries(filters.range ?? {}) as Array<
    [string, NonNullable<SearchFilter["range"]>[string]]
  >) {
    const column = sql.raw(requireSafeIdentifier(field));
    const range = rawRange ?? {};
    if (range.gte !== undefined) {
      clauses.push(sql`${column} >= ${range.gte}`);
    }
    if (range.lte !== undefined) {
      clauses.push(sql`${column} <= ${range.lte}`);
    }
  }

  return clauses;
}

function buildVectorLiteral(vector: number[]): string {
  if (!Array.isArray(vector) || vector.length === 0) {
    throw new SearchRuntimeError(400, "SEARCH_VECTOR_REQUIRED", "vector search requires a non-empty vector");
  }

  if (vector.some((value) => typeof value !== "number" || !Number.isFinite(value))) {
    throw new SearchRuntimeError(400, "SEARCH_INVALID_VECTOR", "vector must contain only finite numbers");
  }

  return `[${vector.join(",")}]`;
}

function buildVectorScore(vectorField: SQL, vectorLiteral: string): SQL {
  return sql`(1 - (${vectorField} <=> CAST(${vectorLiteral} AS vector)))::double precision`;
}

function buildVectorOrder(vectorField: SQL, vectorLiteral: string): SQL {
  return sql`${vectorField} <=> CAST(${vectorLiteral} AS vector)`;
}

function buildRrfFusedScore(rrfK: number, keywordWeight: number, vectorWeight: number): SQL {
  return sql`(
    COALESCE(${keywordWeight}::double precision / (${rrfK} + ${sql.raw("kc.keyword_rank")}), 0) +
    COALESCE(${vectorWeight}::double precision / (${rrfK} + ${sql.raw("vc.vector_rank")}), 0)
  )::double precision`;
}

function normalizeScore(value: unknown): number | undefined {
  if (value === null || value === undefined) return undefined;
  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
}

function sanitizeSearchRow(row: SearchDbRow, hiddenColumns: string[]): Record<string, unknown> {
  const sanitizedRow = { ...row };
  const omittedKeys = new Set<string>([...INTERNAL_SEARCH_ROW_KEYS, ...hiddenColumns]);

  for (const key of omittedKeys) {
    delete sanitizedRow[key];
  }

  return sanitizedRow;
}

function applyMinimumScore(
  rows: SearchDbRow[],
  scoreKey: "score" | "fused_score",
  minScore?: number,
): SearchDbRow[] {
  if (minScore === undefined) {
    return rows;
  }

  return rows.filter((row) => (normalizeScore(row[scoreKey]) ?? Number.NEGATIVE_INFINITY) >= minScore);
}

function mapSearchRows(
  rows: SearchDbRow[],
  meta: SearchResponse["meta"],
  scoreKey: "score" | "fused_score",
  mode: SearchMode,
  hiddenColumns: string[] = [],
): SearchResponse {
  return {
    items: rows.map((row) => {
      const score = normalizeScore(row[scoreKey]) ?? 0;
      const keywordScore = normalizeScore(row.keyword_score);
      const vectorScore = normalizeScore(row.vector_score);
      const itemRow = sanitizeSearchRow(row, hiddenColumns);

      const matchedBy: Array<"keyword" | "vector"> =
        mode === "keyword"
          ? ["keyword"]
          : mode === "vector"
            ? ["vector"]
            : [
                ...(keywordScore !== undefined ? (["keyword"] as const) : []),
                ...(vectorScore !== undefined ? (["vector"] as const) : []),
              ];

      return {
        id: row.id,
        row: itemRow,
        score,
        ...(mode === "hybrid"
          ? {
              scores: {
                ...(keywordScore !== undefined ? { keyword: keywordScore } : {}),
                ...(vectorScore !== undefined ? { vector: vectorScore } : {}),
                fused: score,
              },
            }
          : {}),
        matchedBy,
      } satisfies SearchHit;
    }),
    meta,
  };
}

function createBaseMeta(
  config: SearchTierConfig,
  limit: number,
  offset: number,
  options?: {
    fusedCandidateCount?: number;
    fusion?: "rrf";
    itemCount?: number;
    keywordCandidateCount?: number;
    lexicalStrategy?: LexicalStrategy;
    minScoreApplied?: number;
    mode?: SearchMode;
    vectorCandidateCount?: number;
  },
): SearchResponse["meta"] {
  return {
    engine: config.engine,
    tier: config.plan,
    limit,
    offset,
    ...(options?.itemCount === limit ? { nextOffset: offset + limit } : {}),
    ...(options?.fusion ? { fusion: options.fusion } : {}),
    ...(options?.mode ? { mode: options.mode } : {}),
    ...(options?.lexicalStrategy ? { lexicalStrategy: options.lexicalStrategy } : {}),
    ...(options?.keywordCandidateCount !== undefined
      ? { keywordCandidateCount: options.keywordCandidateCount }
      : {}),
    ...(options?.vectorCandidateCount !== undefined
      ? { vectorCandidateCount: options.vectorCandidateCount }
      : {}),
    ...(options?.fusedCandidateCount !== undefined
      ? { fusedCandidateCount: options.fusedCandidateCount }
      : {}),
    ...(options?.minScoreApplied !== undefined ? { minScoreApplied: options.minScoreApplied } : {}),
  };
}

function getVectorHiddenColumns(vectorField?: string): string[] {
  return ["embedding", vectorField ?? "embedding"];
}

function resolveMetricScope(auth: AuthCtx, scope: SearchOptions["scope"]) {
  if (scope.visibility === "private") {
    return {
      corpus: scope.corpus.trim(),
      visibility: scope.visibility,
      ownerUserId: scope.ownerUserId ?? auth.requireAuth().id,
    };
  }

  return {
    corpus: scope.corpus.trim(),
    visibility: scope.visibility,
    ownerUserId: scope.ownerUserId ?? null,
  };
}

async function observeRetrieveMetrics(
  params: SearchRuntimeDeps,
  input: {
    scope: SearchOptions["scope"];
    table: string;
    meta: SearchResponse["meta"];
    itemCount: number;
    startedAt: number;
  },
): Promise<void> {
  if (!params.observeRagOperation) return;

  const base = {
    ...resolveMetricScope(params.auth, input.scope),
    operation: "retrieve" as const,
    metadata: {
      table: input.table,
      mode: input.meta.mode,
      engine: input.meta.engine,
      tier: input.meta.tier,
    },
  };

  try {
    await Promise.all([
      params.observeRagOperation({
        ...base,
        metricName: "latency_ms",
        metricValue: Math.max(0, Date.now() - input.startedAt),
        unit: "ms",
      }),
      params.observeRagOperation({
        ...base,
        metricName: "result_count",
        metricValue: input.itemCount,
        unit: "count",
      }),
    ]);
  } catch {
    // Observability must not change search behavior.
  }
}

function getVectorSearchAvailabilityError(config: SearchTierConfig): SearchRuntimeError | null {
  if (config.extensions.vector) {
    return null;
  }

  return new SearchRuntimeError(
    503,
    "SEARCH_CAPABILITY_DEGRADED",
    config.degradedReason ?? "Vector search extension is unavailable",
  );
}

function getHybridSearchAvailabilityError(config: SearchTierConfig): SearchRuntimeError | null {
  if (config.plan === "free" && config.hybridSearch === false) {
    return new SearchRuntimeError(
      402,
      "SEARCH_HYBRID_REQUIRES_UPGRADE",
      "Hybrid search requires a plan with lexical + vector search enabled",
    );
  }

  const vectorError = getVectorSearchAvailabilityError(config);
  if (vectorError) {
    return vectorError;
  }

  if (!config.hybridSearch) {
    return new SearchRuntimeError(
      402,
      "SEARCH_HYBRID_REQUIRES_UPGRADE",
      "Hybrid search requires a plan with lexical + vector search enabled",
    );
  }

  return null;
}

function normalizeVectorTuning(options: VectorSearchOptions): NormalizedVectorTuning {
  return {
    minScore: normalizeFiniteNumber(options.tuning?.minScore, "SEARCH_INVALID_MIN_SCORE", "tuning.minScore"),
  };
}

function normalizeHybridTuning(options: HybridSearchOptions, limit: number): NormalizedHybridTuning {
  const fusion = options.tuning?.fusion?.mode ?? options.fusion ?? "rrf";
  if (fusion !== "rrf") {
    throw new SearchRuntimeError(400, "SEARCH_INVALID_FUSION", `Unsupported fusion mode: ${fusion}`);
  }

  return {
    fusion,
    rrfK:
      normalizePositiveInteger(options.tuning?.fusion?.rrfK, "SEARCH_INVALID_RRF_K", "tuning.fusion.rrfK") ??
      RRF_K,
    keywordWeight:
      normalizePositiveNumber(
        options.tuning?.fusion?.keywordWeight,
        "SEARCH_INVALID_FUSION_WEIGHT",
        "tuning.fusion.keywordWeight",
      ) ?? DEFAULT_FUSION_WEIGHT,
    vectorWeight:
      normalizePositiveNumber(
        options.tuning?.fusion?.vectorWeight,
        "SEARCH_INVALID_FUSION_WEIGHT",
        "tuning.fusion.vectorWeight",
      ) ?? DEFAULT_FUSION_WEIGHT,
    keywordCandidateLimit: Math.max(
      limit,
      normalizePositiveInteger(
        options.tuning?.keywordCandidateLimit ?? options.keywordCandidateLimit,
        "SEARCH_INVALID_CANDIDATE_LIMIT",
        "keywordCandidateLimit",
      ) ?? limit * 2,
    ),
    vectorCandidateLimit: Math.max(
      limit,
      normalizePositiveInteger(
        options.tuning?.vectorCandidateLimit ?? options.vectorCandidateLimit,
        "SEARCH_INVALID_CANDIDATE_LIMIT",
        "vectorCandidateLimit",
      ) ?? limit * 2,
    ),
    minFusedScore: normalizeFiniteNumber(
      options.tuning?.minFusedScore,
      "SEARCH_INVALID_MIN_FUSED_SCORE",
      "tuning.minFusedScore",
    ),
  };
}

export function createSearchRuntime(params: SearchRuntimeDeps) {
  return {
    async search(table: string, query: string, options: SearchOptions): Promise<SearchResponse> {
      const startedAt = Date.now();
      const lexicalPlan = resolveLexicalPlan(params.config, query);
      const limit = normalizeLimit(options.limit);
      const offset = normalizeOffset(options.offset);
      const tableIdent = sql.raw(requireSafeIdentifier(table));
      const clauses = [
        buildLexicalClause(lexicalPlan, options.fields, requireSafeIdentifier),
        ...buildScopeClauses(params.auth, options.scope),
        ...buildFilterClauses(options.filters),
      ];
      const lexicalScore = buildLexicalScore(lexicalPlan, options.fields, requireSafeIdentifier);
      const whereClause = joinSql(clauses, " AND ");

      const result = await params.db.execute(sql`
        SELECT *, ${lexicalScore} AS score
        FROM ${tableIdent}
        WHERE ${whereClause}
        ORDER BY score DESC
        LIMIT ${limit} OFFSET ${offset}
      `);

      const rows = rowsFromExecute<SearchDbRow>(result);
      const response = mapSearchRows(
        rows,
        createBaseMeta(params.config, limit, offset, {
          itemCount: rows.length,
          keywordCandidateCount: rows.length,
          lexicalStrategy: lexicalPlan.strategy,
          mode: "keyword",
        }),
        "score",
        "keyword",
        getVectorHiddenColumns(),
      );
      await observeRetrieveMetrics(params, {
        scope: options.scope,
        table,
        meta: response.meta,
        itemCount: response.items.length,
        startedAt,
      });
      return response;
    },

    async vectorSearch(table: string, options: VectorSearchOptions): Promise<SearchResponse> {
      const startedAt = Date.now();
      const vectorError = getVectorSearchAvailabilityError(params.config);
      if (vectorError) {
        throw vectorError;
      }

      const limit = normalizeLimit(options.limit);
      const offset = normalizeOffset(options.offset);
      const tuning = normalizeVectorTuning(options);
      const tableIdent = sql.raw(requireSafeIdentifier(table));
      const vectorField = sql.raw(requireSafeIdentifier(options.vectorField ?? "embedding"));
      const vectorLiteral = buildVectorLiteral(options.vector);
      const vectorScore = buildVectorScore(vectorField, vectorLiteral);
      const vectorOrder = buildVectorOrder(vectorField, vectorLiteral);
      const whereClause = joinSql(
        [
          ...buildScopeClauses(params.auth, options.scope),
          ...buildFilterClauses(options.filters),
          ...(tuning.minScore !== undefined ? [sql`${vectorScore} >= ${tuning.minScore}`] : []),
        ],
        " AND ",
      );

      const result = await params.db.execute(sql`
        SELECT *, ${vectorScore} AS score
        FROM ${tableIdent}
        WHERE ${whereClause}
        ORDER BY ${vectorOrder}
        LIMIT ${limit} OFFSET ${offset}
      `);

      const rows = applyMinimumScore(rowsFromExecute<SearchDbRow>(result), "score", tuning.minScore);
      const response = mapSearchRows(
        rows,
        createBaseMeta(params.config, limit, offset, {
          itemCount: rows.length,
          minScoreApplied: tuning.minScore,
          mode: "vector",
          vectorCandidateCount: rows.length,
        }),
        "score",
        "vector",
        getVectorHiddenColumns(options.vectorField),
      );
      await observeRetrieveMetrics(params, {
        scope: options.scope,
        table,
        meta: response.meta,
        itemCount: response.items.length,
        startedAt,
      });
      return response;
    },

    async hybridSearch(table: string, query: string, options: HybridSearchOptions): Promise<SearchResponse> {
      const startedAt = Date.now();
      const hybridError = getHybridSearchAvailabilityError(params.config);
      if (hybridError) {
        throw hybridError;
      }

      const lexicalPlan = resolveLexicalPlan(params.config, query);
      const limit = normalizeLimit(options.limit);
      const offset = normalizeOffset(options.offset);
      const tuning = normalizeHybridTuning(options, limit);

      const tableIdent = sql.raw(requireSafeIdentifier(table));
      const vectorField = sql.raw(requireSafeIdentifier(options.vectorField ?? "embedding"));
      const vectorLiteral = buildVectorLiteral(options.vector);
      const vectorScoreExpr = buildVectorScore(vectorField, vectorLiteral);
      const vectorOrderExpr = buildVectorOrder(vectorField, vectorLiteral);
      const fusedScoreExpr = buildRrfFusedScore(tuning.rrfK, tuning.keywordWeight, tuning.vectorWeight);

      const sharedClauses = [
        ...buildScopeClauses(params.auth, options.scope),
        ...buildFilterClauses(options.filters),
      ];
      const keywordWhere = joinSql(
        [buildLexicalClause(lexicalPlan, options.fields, requireSafeIdentifier), ...sharedClauses],
        " AND ",
      );
      const vectorWhere = joinSql(sharedClauses, " AND ");
      const keywordScore = buildLexicalScore(lexicalPlan, options.fields, requireSafeIdentifier);
      const rankedWhereClause = joinSql(
        tuning.minFusedScore !== undefined ? [sql`fused_score >= ${tuning.minFusedScore}`] : [],
        " AND ",
      );

      const result = await params.db.execute(sql`
        WITH keyword_candidates AS (
          SELECT id,
                 ${keywordScore} AS keyword_score,
                 ROW_NUMBER() OVER (ORDER BY ${keywordScore} DESC) AS keyword_rank
          FROM ${tableIdent}
          WHERE ${keywordWhere}
          LIMIT ${tuning.keywordCandidateLimit}
        ),
        vector_candidates AS (
          SELECT id,
                 ${vectorScoreExpr} AS vector_score,
                 ROW_NUMBER() OVER (ORDER BY ${vectorOrderExpr}) AS vector_rank
          FROM ${tableIdent}
          WHERE ${vectorWhere}
          LIMIT ${tuning.vectorCandidateLimit}
        ),
        ranked AS (
          SELECT t.*,
                 kc.keyword_score,
                 vc.vector_score,
                 ${fusedScoreExpr} AS fused_score
          FROM ${tableIdent} t
          LEFT JOIN keyword_candidates kc ON kc.id = t.id
          LEFT JOIN vector_candidates vc ON vc.id = t.id
          WHERE kc.id IS NOT NULL OR vc.id IS NOT NULL
        )
        SELECT *
        FROM ranked
        WHERE ${rankedWhereClause}
        ORDER BY fused_score DESC
        LIMIT ${limit} OFFSET ${offset}
      `);

      const rows = applyMinimumScore(
        rowsFromExecute<SearchDbRow>(result),
        "fused_score",
        tuning.minFusedScore,
      );
      const keywordCandidateCount = rows.filter(
        (row) => normalizeScore(row.keyword_score) !== undefined,
      ).length;
      const vectorCandidateCount = rows.filter(
        (row) => normalizeScore(row.vector_score) !== undefined,
      ).length;
      const response = mapSearchRows(
        rows,
        createBaseMeta(params.config, limit, offset, {
          fusedCandidateCount: rows.length,
          fusion: tuning.fusion,
          itemCount: rows.length,
          keywordCandidateCount,
          lexicalStrategy: lexicalPlan.strategy,
          minScoreApplied: tuning.minFusedScore,
          mode: "hybrid",
          vectorCandidateCount,
        }),
        "fused_score",
        "hybrid",
        getVectorHiddenColumns(options.vectorField),
      );
      await observeRetrieveMetrics(params, {
        scope: options.scope,
        table,
        meta: response.meta,
        itemCount: response.items.length,
        startedAt,
      });
      return response;
    },
  };
}
